// DashboardHome.jsx
import React, { useState, useEffect } from 'react';
import { 
    Users,
    DollarSign,
    TrendingUp,
    ArrowUpRight,
    ArrowDownRight,
    CreditCard,
    Building2,
    Activity,
    StarHalf,
    Rocket,
    Siren,
    LandPlot,
    ChartSpline,
    Coins
} from 'lucide-react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    ComposedChart
} from 'recharts';
import { formatCurrency, formatDateIntl, formatPercentage } from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import './DashboardHome.css';
import CurrencySelector from '../../components/CurrencySelector';

const DashboardAdminPage = () => {
    const [loading, setLoading] = useState({
        stats: true,
        trends: true,
        distribution: true,
        activity: true,
        top: true
    });
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [topLine, setTopLine] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('GBP');
    const [selectedInterval, setSelectedInterval] = useState('14');
    const [trends, setTrends] = useState([]);
    const [distribution, setDistribution] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [timeframe, setTimeframe] = useState('month'); // week, month, year

    useEffect(() => {
        fetchDashboardData(selectedCurrency);
        fetchInvestmentGrowth(selectedCurrency);
        fetchRecentActivies(selectedInterval);
        fetchFundDistribution(selectedCurrency);
        fetchTopLineData(selectedCurrency);
    }, [timeframe, selectedCurrency]);


    useEffect(() => {
        fetchTopLineData(selectedCurrency);
        fetchRecentActivies(selectedInterval);
    }, [selectedInterval]);

    const fetchFundDistribution = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const distributionResponse = await fetch(
                `${baseUrl}/api/v1/analytics/${targetCurrency}/fund-distribution`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!distributionResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, distribution: false }));
                return;
            }
            const distributionData = await distributionResponse.json();
            console.log(distributionData);
            setDistribution(distributionData);
            setLoading(prev => ({ ...prev, distribution: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, distribution: false }));
        }
    };

    const fetchInvestmentGrowth = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const trendsResponse = await fetch(
                `${baseUrl}/api/v1/analytics/${targetCurrency}/investment-growth`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!trendsResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, trends: false }));
                return;
            }
            const trendsData = await trendsResponse.json();
            setTrends(trendsData);
            setLoading(prev => ({ ...prev, trends: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, trends: false }));
        }
    };

    const fetchRecentActivies = async (interval) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const activiesResponse = await fetch(
                `${baseUrl}/api/v1/activity/${interval}/recent-activities`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!activiesResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, activity: false }));
                return;
            }
            const activityData = await activiesResponse.json();
            setRecentActivity(activityData);
            setLoading(prev => ({ ...prev, activity: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, activity: false }));
        }
    };

    const fetchDashboardData = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            
            // Fetch basic stats
            const statsResponse = await fetch(`${baseUrl}/api/v1/analytics/${targetCurrency}/overview`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!statsResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, stats: false }));
                return;
            }
            const statsData = await statsResponse.json();
            setStats(statsData);
            setLoading(prev => ({ ...prev, stats: false }));
        } catch (err) {
            setError(err.message);
            setLoading({
                stats: false
            });
        }
    };

    const fetchTopLineData = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            
            // Fetch basic stats
            const topLineResponse = await fetch(`${baseUrl}/api/v1/analytics/${targetCurrency}/top-line`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!topLineResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, top: false }));
                return;
            }
            const topLineData = await topLineResponse.json();
            console.log(topLineData);
            setTopLine(topLineData);
            setLoading(prev => ({ ...prev, top: false }));
        } catch (err) {
            setError(err.message);
            setLoading({
                top: false
            });
        }
    };

    const isLoading = Object.values(loading).some(status => status);

    if (isLoading) {
        return <Loader loading={true} />;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

    const handleCurrencyChange = (currency) => {
        setSelectedCurrency(currency);
    };

    return (
            <div className="dashboard-home">
            {/* Stats Grid */}

            <div className="dashboard-header">
                <h1>Dashboard</h1>
                <CurrencySelector 
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={handleCurrencyChange}
                />
            </div>

            <div className="dashboard-stats-grid">
                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Coins />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Volume</div>
                        <div className="dashboard-stat-value">
                            {formatCurrency(topLine.total_volume, selectedCurrency)}
                        </div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <ChartSpline color='red'/>
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Buys</div>
                        <div className="dashboard-stat-value">{formatCurrency(topLine.total_investments)}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <LandPlot />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Sold</div>
                        <div className="dashboard-stat-value">{topLine.total_redemptions}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Siren />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Funds Involved</div>
                        <div className="dashboard-stat-value">
                            {topLine.fund}
                        </div>
                    </div>
                </div>
                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Rocket />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Number Transaction</div>
                        <div className="dashboard-stat-value">
                            {topLine.number_investments}
                        </div>
                    </div>
                </div>
                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <StarHalf />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Number Redemption</div>
                        <div className="dashboard-stat-value">
                            {topLine.number_redemptions}
                        </div>
                    </div>
                </div>
            </div>

            <div className='dashboard-stats-grid-header'>
                <h3>Investment Two Month Trends</h3>
            </div>
            <div className="dashboard-stats-grid">

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <DollarSign />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Investments</div>
                        <div className="dashboard-stat-value">
                            {formatCurrency(stats.total_investments, selectedCurrency)}
                        </div>
                        <div className={`stat-change ${stats.investment_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.investment_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.investment_change))}
                        </div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Users />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Users</div>
                        <div className="dashboard-stat-value">{stats.total_users}</div>
                        <div className={`stat-change ${stats.user_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.user_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.user_change))}
                        </div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Building2 />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Active Funds</div>
                        <div className="dashboard-stat-value">{stats.active_funds}</div>
                        <div className={`stat-change ${stats.fund_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.fund_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.fund_change))}
                        </div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Activity />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Transaction Volume</div>
                        <div className="dashboard-stat-value">
                            {formatCurrency(stats.transaction_volume, selectedCurrency)}
                        </div>
                        <div className={`stat-change ${stats.volume_change >= 0 ? 'positive' : 'negative'}`}>
                            {stats.volume_change >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                            {formatPercentage(Math.abs(stats.volume_change))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
                {/* Investment Trends */}
                <div className="chart-card large">
                    <div className="chart-header">
                        <h3>Investment Trends</h3>
                        <select 
                            value={timeframe} 
                            onChange={(e) => setTimeframe(e.target.value)}
                            className="timeframe-select"
                        >
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="year">This Year</option>
                        </select>
                    </div>
                    <div className="chart-content">
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart data={trends.metrics}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(date) => formatDateIntl(date, 'short')}
                                />
                                <YAxis
                                    yAxisId="left"
                                    orientation="right" 
                                    tickFormatter={(value) => formatCurrency(value, selectedCurrency, true)}
                                />
                                <YAxis
                                    yAxisId="right" 
                                    tickFormatter={(value) => formatCurrency(value, selectedCurrency, true)}
                                />
                                <Tooltip 
                                    formatter={(value) => formatCurrency(value, selectedCurrency)}
                                    labelFormatter={(date) => formatDateIntl(date)}
                                />

                                <Line
                                    yAxisId="left"
                                    type="monotone" 
                                    dataKey="cumulative_investment" 
                                    stroke="#f87171" 
                                    name="Cumulative Investment"
                                    strokeDasharray="5 5"
                                    activeDot={{ r: 8 }}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone" 
                                    dataKey="daily_net" 
                                    stroke="#82ca9d"
                                    name="Daily Net"
                                    strokeDasharray="5 5"
                                    activeDot={{ r: 8 }}
                                />
                               <Area
                                    yAxisId="right" 
                                    type="monotone" 
                                    dataKey="daily_net" 
                                    stroke="#6366f1" 
                                    fill="#6366f1" 
                                    fillOpacity={0.1}
                                />
                                <Legend />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Fund Distribution */}
                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Fund Distribution</h3>
                    </div>
                    <div className="chart-content">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={distribution}
                                    dataKey="percentage"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label
                                >
                                    {distribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value, selectedCurrency)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="activity-section">
                <div className="section-header">
                    <h3>Recent Activity</h3>
                    <select 
                            value={selectedInterval} 
                            onChange={(e) => setSelectedInterval(e.target.value)}
                            className="timeframe-select"
                        >
                            <option value="1">Today</option>
                            <option value="7">This Week</option>
                            <option value="14">Last Two Week</option>
                            <option value="30">Last Month</option>
                    </select>
                </div>
                <div className="activity-list">
                    {recentActivity.map((activity, index) => (
                        <div key={index} className="activity-item">
                            <div className="activity-icon">
                                {activity.type === 'investment' ? <DollarSign /> : 
                                 activity.type === 'user' ? <Users /> : <Building2 />}
                            </div>
                            <div className="activity-content">
                                <div className="activity-title">{activity.description}</div>
                                <div className="activity-time">
                                    {formatDateIntl(activity.timestamp)}
                                </div>
                            </div>
                            <div className="activity-amount">
                                {activity.amount != -1 ? formatCurrency(activity.amount, activity.currency_code): ""}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DashboardAdminPage;
